import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useIntl } from 'react-intl';

import { API } from 'src/reactQuery/Api';
import download from 'src/utils/download';
import { getRequest } from 'src/utils/request';
import { QUERY_KEYS } from 'src/reactQuery/QueryKeys';
import {
  ErrorType,
  useMessageHandler,
} from 'src/utils/hooks/useMessageHandler';
import messages from 'src/pages/ProjectDetailsPage/messages';

export type DownloadVerificationCodeFetchParamsType = {
  projectId: string;
  projectUuid: string;
  projectVerificationCode: string;
};

const downloadVerificationCode = async ({
  projectId,
  projectUuid,
  projectVerificationCode,
}: DownloadVerificationCodeFetchParamsType) => {
  const code = projectVerificationCode
    ? `Code_${projectVerificationCode}`
    : 'No_Code';

  const data = await getRequest(API.GET.downloadVerificationCode(projectUuid), {
    responseType: 'blob',
  });
  download(
    data.data,
    `QR_Code_PID_${projectId}_${code}.pdf`,
    'application/pdf',
  );
  return data.data;
};

export const useDownloadVerificationCode = ({
  params,
  onSettled,
  enabled = false,
}: {
  params: DownloadVerificationCodeFetchParamsType;
  onSettled?: () => void;
  enabled?: boolean;
}) => {
  const { formatMessage } = useIntl();
  const client = useQueryClient();
  const { handleError, handleSuccess } = useMessageHandler();

  const { isSuccess, isError, error } = useQuery<unknown, ErrorType>({
    queryKey: [QUERY_KEYS.DOWNLOAD_VERIFICATION_CODE],
    queryFn: () => downloadVerificationCode(params),
    enabled,
  });

  useEffect(() => {
    if (isSuccess) {
      handleSuccess(formatMessage(messages.verificationCodeDownloaded), 1000);
      client.removeQueries({
        queryKey: [QUERY_KEYS.DOWNLOAD_VERIFICATION_CODE],
      });
    }
    if ((isSuccess || isError) && onSettled) {
      onSettled();
    }
    if (isError && error) {
      handleError(error);
    }
  }, [isSuccess, isError]);
};
